import { useEffect, useMemo, useRef } from 'react'
import useAuthHook from '../../hooks/use-auth-hook'
import { RouterProvider } from '@tanstack/react-router'
import { customQueryClient } from '../../react-query/config/query-client.jsx'
import { router } from '../../router'

export const App = () => {
  // Auth Hook
  const { user, isLoggedIn, isLoadingLogin, tokenAvailable } = useAuthHook()

  const routerContext = useMemo(
    () => ({
      queryClient: customQueryClient,
      auth: {
        user,
        isLoggedIn,
        isLoadingLogin,
        tokenAvailable
      }
    }),
    [user, isLoggedIn, isLoadingLogin, tokenAvailable]
  )

  const authStateRef = useRef({
    isLoggedIn,
    isLoadingLogin,
    tokenAvailable
  })

  /**
   * Revalidates TanStack Router guards when auth state changes.
   * `beforeLoad` only runs on navigation, so we manually invalidate and reload
   * the current matches after login/logout or when initial auth loading finishes.
   */

  useEffect(() => {
    const prev = authStateRef.current
    const finishedLoading = prev.isLoadingLogin && !isLoadingLogin
    const loggedInChanged = prev.isLoggedIn !== isLoggedIn
    const tokenChanged = prev.tokenAvailable !== tokenAvailable

    authStateRef.current = { isLoggedIn, isLoadingLogin, tokenAvailable }

    if (isLoadingLogin) return
    if (!(finishedLoading || loggedInChanged || tokenChanged)) return

    const revalidateRouter = async () => {
      await router.invalidate({ forcePending: true })
      await router.load()
    }

    revalidateRouter()
  }, [isLoggedIn, isLoadingLogin, tokenAvailable])

  return <RouterProvider router={router} context={routerContext} />
}

export default App
